import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';

import { usePrompt } from 'hooks/usePrompt';

import { isLocationChangeBlocked as isLocationChangeBlockedSelector } from 'modules/app/selectors';

import Button from './Button';
import Popconfirm from './Popconfirm';

/** @type {React.FC<{ warning?: boolean | (() => boolean), prompt?: Object, forceRedirect?: boolean } & import("antd").ButtonProps >} */
const FBBackButton = ({ onClick, warning, prompt, forceRedirect = false, ...rest }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const isLocationChangeBlocked = useSelector(isLocationChangeBlockedSelector);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [mountPrompt, setMountPrompt] = React.useState(true);

  const hasPrompt = typeof prompt === 'object';
  const historyLength = global.historyLocalLength;

  const promptProps = React.useMemo(() => {
    let when = (typeof warning === 'function' ? warning() : warning) ?? false;

    if (prompt && prompt.when) {
      when = prompt.when;
    }

    // If `isLocationChangeBlocked` is true, then we warn the user, otherwise we take into account the other props.
    when = isLocationChangeBlocked || when;

    return ({
      message: location =>
        currentLocation.pathname !== location.pathname &&
        intl.formatMessage({
          id: 'audito.model.preventBack',
          defaultMessage: 'Leaving this page, all unsaved data will be lost.',
        }),
      ...(prompt ?? {}),
      when
    });
  }, [currentLocation.pathname, intl, prompt, warning, isLocationChangeBlocked]);

  usePrompt(promptProps.message, mountPrompt && hasPrompt && promptProps.when);

  const handleCancel = React.useCallback(() => {
    setMountPrompt(true);
    setShowConfirm(false);
  }, []);

  const goBack = React.useCallback(event => {
    if (forceRedirect || historyLength <= (global.initialHistoryLength ?? 2)) {
      onClick(event);
    } else {
      navigate(-1);
    }
  }, [forceRedirect, historyLength, navigate, onClick]);

  const handleClick = React.useCallback(visible => {
    let shouldShowWarning = isLocationChangeBlocked;
    if (!shouldShowWarning) {
      shouldShowWarning = typeof warning === 'function' ? warning() : warning;
    }

    setMountPrompt(false);

    if (visible) {
      if (shouldShowWarning) {
        setShowConfirm(true);
      } else {
        goBack();
      }
    } else {
      setShowConfirm(false);
    }
  }, [goBack, isLocationChangeBlocked, warning]);

  return (
    <Popconfirm
      okText={<FormattedMessage id="ok" defaultMessage="Ok" />}
      onConfirm={goBack}
      open={showConfirm}
      cancelText={<FormattedMessage id="cancel" defaultMessage="Cancel" />}
      placement="bottomRight"
      onCancel={handleCancel}
      onOpenChange={handleClick}
      title={<FormattedMessage id="audito.model.preventBack" defaultMessage="Leaving this page, all unsaved data will be lost." />}
    >
      <Button
        {...rest}
        className={classNames('flex-shrink-0', rest.className)}
        icon={<i className="fb-icon fb-icon--arrow-line-left" />}
      />
    </Popconfirm>
  );
};

export default FBBackButton;
